/**
 * Re-attaching the Solana wallet of a holder who already trusts this site.
 *
 * Phantom does reconnect a trusted site by itself, but not before the page
 * has loaded: measured against the real extension on mudwater.bayc.com,
 * `isConnected` turned true around 1.3s after navigation. The first version
 * of this read that flag once, from an effect that runs on mount, and so read
 * it while it was still false — the re-attach almost never happened, and a
 * returning holder was shown a logged-out site and the sign-in prompt all
 * over again. Which load it did work on was down to the race, which is why it
 * passed by hand.
 *
 * Waiting is what `connect({ onlyIfTrusted: true })` does. It resolves once
 * the wallet has reattached a site the holder has already approved, rejects
 * for one they have not, and raises no approval window in either case. That
 * last part is why this is safe to run on load, having clicked nothing, and
 * is what the adapter's own `autoConnect` gets wrong: it calls a bare
 * `connect()`. `isConnected` is still read first, because a wallet that has
 * already reattached needs no call at all.
 */

/**
 * Phantom's injected provider, and only Phantom's.
 *
 * `window.solana` is whichever Solana wallet claimed it, and the guarantee
 * this module rests on is Phantom's. A wallet that does not implement
 * `onlyIfTrusted` would read the call as a plain `connect()` and raise an
 * approval window on page load, having been clicked nothing — the one thing
 * this must never do. Checking `isPhantom` costs a re-attach for any wallet
 * without the flag, which is the right way to lose.
 *
 * The bare flag, and not `isGenuinePhantom`: Exodus and Magic Eden can hold
 * `window.phantom.solana` with `isPhantom` set, and so can be reached here.
 * They always were, and the Phantom button connects them on a click, because
 * the adapter checks the same flag. Nothing shows their `onlyIfTrusted` raising
 * a window, so refusing them would only log their holders out on every load.
 *
 * The order matches the adapter's own (`window.phantom?.solana ||
 * window.solana`), so this and `adapter.connect()` always reach the same
 * object.
 */
function injectedPhantom(): object | null {
  if (typeof window === "undefined") return null;
  const phantom: unknown = Reflect.get(window, "phantom");
  const solana: unknown =
    (typeof phantom === "object" && phantom !== null
      ? Reflect.get(phantom, "solana")
      : undefined) ?? Reflect.get(window, "solana");
  if (typeof solana !== "object" || solana === null) return null;
  return Reflect.get(solana, "isPhantom") === true ? solana : null;
}

/**
 * Reconnects, but only to a site the holder has already approved.
 *
 * Answers false rather than throwing on every refusal, because "not trusted"
 * is the ordinary case for a first-time visitor and not an error. A resolved
 * promise is not taken as enough on its own: the wallet must also report
 * itself connected, so that the adapter's `connect()` the caller runs next
 * takes its `if (!wallet.isConnected)` branch as false and reads the key
 * without asking. A wallet that resolves without connecting costs us the
 * re-attach, which is again the right way to lose.
 */
export async function reconnectIfTrusted(): Promise<boolean> {
  const solana = injectedPhantom();
  if (!solana) return false;
  if (Reflect.get(solana, "isConnected") === true) return true;
  const connect: unknown = Reflect.get(solana, "connect");
  if (typeof connect !== "function") return false;
  try {
    await connect.call(solana, { onlyIfTrusted: true });
  } catch {
    // The holder has not trusted this site. Nothing was shown to them.
    return false;
  }
  return Reflect.get(solana, "isConnected") === true;
}

/**
 * Logging out has to outlast the page, because Phantom's own trust does.
 * Without this a holder who logged out and reloaded would be reattached by
 * the wallet they had just left.
 *
 * Per tab: closing it is as deliberate an end as logging out, and a holder
 * who logs out in one tab has said nothing about another.
 */
const SIGNED_OUT = "mudwater.solana.signed-out";

export function rememberSignedOut(): void {
  try {
    sessionStorage.setItem(SIGNED_OUT, "1");
  } catch {
    // A blocked store only costs the reattach, never correctness.
  }
}

export function forgetSignedOut(): void {
  try {
    sessionStorage.removeItem(SIGNED_OUT);
  } catch {
    // As above.
  }
}

export function hasSignedOut(): boolean {
  try {
    return sessionStorage.getItem(SIGNED_OUT) === "1";
  } catch {
    // Unreadable means unknown, and an unknown must not reattach silently.
    return true;
  }
}

/**
 * A holder who declined Phantom's sign-in once in this tab. `solana:signIn`
 * connects and signs in one prompt, so a refusal also left them unconnected,
 * and the next Connect asked the same question again: one holder declined it
 * five times running (Faro, 2026-10-18). The row it records authorizes
 * nothing, so after a refusal Connect simply connects.
 */
const DECLINED_SIGN_IN = "mudwater.solana.sign-in-declined";

export function rememberDeclinedSignIn(): void {
  try {
    sessionStorage.setItem(DECLINED_SIGN_IN, "1");
  } catch {
    // A blocked store only means the sign-in is offered again.
  }
}

export function hasDeclinedSignIn(): boolean {
  try {
    return sessionStorage.getItem(DECLINED_SIGN_IN) === "1";
  } catch {
    return false;
  }
}

/**
 * A browser that has had a wallet connected here before, so a silent reattach
 * is likely on the next load. A cookie rather than storage because the server
 * reads it too: it renders the connected page's loading state for a returning
 * holder instead of the disconnected screen, which swapped out from under
 * them a second later (the largest layout shift on /inventory/, Faro).
 *
 * A layout hint only, never proof of anything: a stale one costs a few seconds
 * of loading state before the disconnected screen.
 */
export const RETURNING_COOKIE = "mudwater_returning";

export function rememberReturning(): void {
  try {
    document.cookie = `${RETURNING_COOKIE}=1; path=/; max-age=31536000; samesite=lax`;
  } catch {
    // A blocked cookie only costs the reserved layout.
  }
}

export function forgetReturning(): void {
  try {
    document.cookie = `${RETURNING_COOKIE}=; path=/; max-age=0; samesite=lax`;
  } catch {
    // As above.
  }
}
